import { ReactNode, useMemo, useState } from 'react';
import cx from 'classnames';

import { EventUserSchema, OnStageUser, UserRole } from 'types';
import { convertEventUserToStageUser, convertInviteeToStageUser, MAX_STAGE_SEATS } from 'utils';
import {
  eventSessionSelector,
  filledSeatsCountSelector,
  inviteesSelector,
  pendingUsersSelector,
  userSelector
} from 'store/selectors';
import { LoggerService } from 'services';
import { useConfirmation, useStore, useToast } from 'hooks';
import { ConfirmPanel, Guest } from 'containers';
import { useLiveEvent } from 'containers/LiveEvent';
import { AddPeopleIcon, PlusIcon } from 'components/Icon';

import styles from './Guests.module.scss';

export type GuestsPanelProps = {
  stageUsers?: OnStageUser[];
  viewers?: EventUserSchema[];
  totalViewersCount?: number;
  anonymousViewersCount?: number;
  onShare?(): void;
  onAddMember?(role: UserRole.MODERATOR | UserRole.SPEAKER | UserRole.VIEWER, seatIndex?: number): void;
  onRemoveFromStage?(user: OnStageUser): void | Promise<void>;
  onSwitchRole?(user: OnStageUser, promote: boolean): void;
  onInviteViewerToStage?(username: string): void;
  onMute?(user: OnStageUser): void;
};

type Tab = 'stage' | 'viewers';

const GuestsPanel = ({
  stageUsers = [],
  viewers = [],
  totalViewersCount = 0,
  anonymousViewersCount = 0,
  onShare,
  onAddMember,
  onRemoveFromStage,
  onSwitchRole,
  onInviteViewerToStage,
  onMute
}: GuestsPanelProps): JSX.Element => {
  const toast = useToast();
  const confirmation = useConfirmation<'REMOVE_FROM_STAGE', { selectedStageUser: OnStageUser }>();
  const liveEvent = useLiveEvent();
  const eventSession = useStore(eventSessionSelector(liveEvent.eventCode));
  const localUser = useStore(userSelector(liveEvent.eventCode, eventSession.username));
  const { filledSeatsCount, invitees, pendingUsers } = useStore(state => ({
    filledSeatsCount: filledSeatsCountSelector(liveEvent.eventCode)(state),
    invitees: inviteesSelector(liveEvent.eventCode)(state),
    pendingUsers: pendingUsersSelector(liveEvent.eventCode)(state)
  }));

  const [activeTab, setActiveTab] = useState<Tab>('stage');
  const [moreOptions, setMoreOptions] = useState({ isOpen: false, username: '' });

  const filledSeats = useMemo(
    () => [
      ...stageUsers.map<OnStageUser>(stageUser => ({ ...stageUser, type: 'stageUser' })),
      ...invitees.map<OnStageUser>(invitee => ({ ...convertInviteeToStageUser(invitee), type: 'invitee' })),
      ...pendingUsers.map<OnStageUser>(pendingUser => ({
        ...convertEventUserToStageUser(pendingUser),
        type: 'pending'
      }))
    ],
    [stageUsers, invitees, pendingUsers]
  );

  const tabs: Array<{
    name: Tab;
    title: string | ReactNode;
  }> = useMemo(() => {
    switch (localUser.role) {
      case UserRole.HOST:
      case UserRole.MODERATOR:
      case UserRole.SPEAKER: {
        return [
          {
            name: 'stage',
            title: (
              <>
                On stage <span className={styles.count}>({stageUsers.length})</span>
              </>
            )
          },
          {
            name: 'viewers',
            title: (
              <>
                Viewers <span className={styles.count}>({totalViewersCount})</span>
              </>
            )
          }
        ];
      }

      default: {
        return [
          {
            name: 'stage',
            title: (
              <>
                On stage <span className={styles.count}>({stageUsers.length})</span>
              </>
            )
          }
        ];
      }
    }
  }, [localUser.role, stageUsers.length, totalViewersCount]);

  const handleOnInviteNewGuest = () => {
    if (filledSeatsCount >= MAX_STAGE_SEATS) {
      toast.info('No seats are available on stage. Please remove someone from the active stage to invite a speaker', {
        id: 'NO_AVAILABLE_SEATS_ON_STAGE'
      });
    } else {
      onAddMember?.(UserRole.SPEAKER, filledSeatsCount + 1);
    }
  };

  const handleOnInviteViewerToStage = (viewer: EventUserSchema) => {
    if (filledSeatsCount >= MAX_STAGE_SEATS) {
      toast.info('No seats are available on stage. Please remove someone from the active stage to invite a speaker', {
        id: 'NO_AVAILABLE_SEATS_ON_STAGE'
      });
    } else {
      onInviteViewerToStage?.(viewer.username);
    }
  };

  const handleOnToggleMoreOptions = (stageUser: OnStageUser) => {
    if (moreOptions.isOpen && moreOptions.username === stageUser.username) {
      setMoreOptions({
        isOpen: false,
        username: ''
      });
    } else {
      setMoreOptions({
        isOpen: true,
        username: stageUser.username
      });
    }
  };

  const handleOnRemoveFromStage = (stageUser: OnStageUser) => {
    confirmation.open('REMOVE_FROM_STAGE', { selectedStageUser: stageUser });
  };

  const handleOnRemovalCancelled = () => {
    confirmation.close();
  };

  const handleOnRemovalSubmitted = async () => {
    const { selectedStageUser } = confirmation.state.params || {};

    if (selectedStageUser) {
      try {
        await onRemoveFromStage?.(selectedStageUser);
        confirmation.close();
      } catch (e) {
        LoggerService.log('REMOVE_FROM_STAGE', e);
        if (e instanceof Error) {
          toast.error(e.message, { id: 'REMOVE_FROM_STAGE' });
        }
      }
    }
  };

  const handleOnShare = () => {
    onShare?.();
  };

  const renderSeatsCount = () => {
    switch (localUser.role) {
      case UserRole.HOST:
      case UserRole.MODERATOR:
        return (
          <p className={styles.count}>
            <span>{filledSeatsCount}</span> out of <span>{MAX_STAGE_SEATS}</span> seats filled
          </p>
        );

      default:
        return null;
    }
  };

  const renderInviteStageGuests = () => {
    switch (localUser.role) {
      case UserRole.HOST:
      case UserRole.MODERATOR: {
        if (filledSeatsCount >= MAX_STAGE_SEATS) return null;

        return (
          <button type="button" className={styles.add} onClick={handleOnInviteNewGuest}>
            <PlusIcon />
            <span>Invite guests</span>
          </button>
        );
      }

      default: {
        return null;
      }
    }
  };

  const renderStageUsersList = () => (
    <div className={cx(styles.guests, styles.stage)}>
      <ul className={styles.list}>
        {filledSeats.map((stageUser, seatIndex) => (
          <li key={stageUser.username}>
            <Guest.Stage
              stageUser={stageUser}
              seatIndex={seatIndex}
              isMoreOptionsOpen={moreOptions.isOpen && moreOptions.username === stageUser.username}
              onToggleMoreOptions={handleOnToggleMoreOptions}
              onMute={onMute}
              onRemoveFromStage={handleOnRemoveFromStage}
              onSwitchRole={onSwitchRole}
            />
          </li>
        ))}
      </ul>
      {renderSeatsCount()}
      {renderInviteStageGuests()}
    </div>
  );

  const renderViewersList = () => {
    switch (localUser.role) {
      case UserRole.HOST:
      case UserRole.MODERATOR:
      case UserRole.SPEAKER:
        return (
          <div className={cx(styles.guests, styles.viewers)}>
            <button type="button" className={styles.add} onClick={handleOnShare}>
              <AddPeopleIcon />
              <span>Invite viewers</span>
            </button>
            <ul className={styles.list}>
              {viewers.map(viewer => (
                <li key={viewer.username}>
                  <Guest.Viewer viewer={viewer} onInvite={handleOnInviteViewerToStage} />
                </li>
              ))}
            </ul>
          </div>
        );

      case UserRole.VIEWER:
      default:
        return null;
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'stage': {
        return <>{renderStageUsersList()}</>;
      }

      case 'viewers':
        return <>{renderViewersList()}</>;

      default:
        return null;
    }
  };

  return (
    <>
      <div className={styles.container}>
        <ul className={styles.tabs}>
          {tabs.map(tab => (
            <li key={tab.name}>
              <button
                type="button"
                className={cx(styles.tab, { [styles.active]: activeTab === tab.name })}
                onClick={() => setActiveTab(tab.name)}
              >
                {tab.title}
              </button>
            </li>
          ))}
        </ul>
        <div className={styles.content}>{renderContent()}</div>
      </div>

      <ConfirmPanel
        show={confirmation.state.isOpen}
        title="Do you want to remove?"
        name="REMOVE_FROM_STAGE"
        message={
          <span>
            If you remove, <b>{confirmation.state.params?.selectedStageUser?.name}</b> will not able to turn on their
            microphone
          </span>
        }
        submitText="Remove"
        onCancel={handleOnRemovalCancelled}
        onSubmit={handleOnRemovalSubmitted}
      />
    </>
  );
};

export default GuestsPanel;
